import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import queryString from 'query-string';
import moment from 'moment';
import { Swiper, SwiperSlide } from 'swiper/react';
import { Mousewheel, Navigation } from 'swiper/modules';
import { isIOS } from 'react-device-detect';

import useFetchPlayerConfigData from './components/Player/hooks/useFetchPlayerConfigurationData';
import useFetchInteractProjectData from './components/Player/hooks/useFetchInteractProjectData';
import useProcessMediaData from './components/Player/hooks/useProcessMediaData';
import ShareMediaDialog from './components/Controls/ShareMediaDialog';
import { QualitySettingMenu } from './components/Controls/SettingsMenu/QualitySettingMenu';
import { LanguageSettingMenu } from './components/Controls/SettingsMenu/LanguageSettingMenu';
import Poster from './components/Poster/Poster';
import CountDown from './components/CountDown/CountDown';
import TransitionLayer from './components/Interact/InteractTransition/TransitionLayer';
import Player from './components/Player/Player';
import Controls from './components/Controls/Controls';
import ShoppingBasket from './components/ShoppingBasket/ShoppingBasket';
import ChatOverlay from './components/ChatOverlay/ChatOverlay';
import Playlist from './components/Playlist/Playlist';
import StoryFeed from './components/StoryFeed/StoryFeed';
import { getStoryThumbnailUrl, isReelsFeedEnabled } from './components/StoryFeed/story-feed-helper';
import PasswordRestrictionContainer from "./components/RestrictionComponents/PasswordRestrictionContainer";
import { usePlayer } from './contexts/PlayerContext';
import { useConfiguration } from './contexts/ConfigurationContext';
import { useRestriction } from './contexts/RestrictionContext';
import { usePresenter } from './contexts/PresenterContext';
import useGoogleAnalytic from './hooks/useGoogleAnalytic';
import { useStickyVideoPlayer } from './hooks/useStickyVideoPlayer';
import StickyPlayerControls from './components/StickyPlayer/StickyPlayerControls';
import {
	EDITOR_PLAYER_ID,
	getAspectRatioValue,
	getConfigParams,
	getPlayerBorderRadius,
	getSecondsFromTimelineTime,
	getTimelineTimeFromTimeInput,
	isUsedOnMobile as isUsedOnMobileFunc,
	joinClassNames,
	SCREEN_TYPE,
	STORY_CONFIG_ID,
	STORY_SLIDE_WINDOW
} from './util/util';
import { getMediaRestrictToken } from './util/media-data-util';
import { MessageHelper } from './util/message-helper';
import { TranscriptionContainer } from "./components/TranscriptionModule/TranscriptionContainer";
import { useFetchTranscriptData } from "./components/Controls/SettingsMenu/useFetchTranscriptData";
import SubtitleSettingMenu from './components/Controls/SettingsMenu/SubtitleSettingMenu';
import Presenter from './components/Presenter/Presenter';
import InteractOverlay from './components/Interact/InteractOverlay/InteractOverlay';
import RegisterForm from './components/Form/RegisterForm';
import ErrorContainer from "./components/RestrictionComponents/ErrorContainer";

import 'swiper/css';
import './App.scss';

interface AppProps {
	qplayerId: string;
	qplayerMediaId: string;
	qplayerScenarioId: string;
	qplayerSceneId: string;
	qplayerPlaylistId: string;
	qplayerAccountId: string;
	qplayerPresetId: string;
	qplayerPreset: string;
	qplayerLanguage: string;
	qplayerShareUrl: string;
	qplayerInline?: string;
	qplayerSharing?: string;
	qplayerAutoplay?: string;
	qplayerMute?: string;
	qplayerRepeat?: string;
	qplayerDownload?: string;
	qplayerOpenInNewWindow?: string;
	qplayerThumbnailMediaId?: string;
	qplayerThumbnailWidth?: string;
	qplayerThumbnailHeight?: string;
	qplayerAnalytics?: string;
	qplayerIgnoreChat?: string;
	qplayerChatData?: string;
	qplayerUniqueFrame?: string;
	qplayerToken?: string;
	qplayerTitle?: string;
	qplayerStartAt?: string;
	qplayerCustomButton?: string;
	qplayerAnalyticsCookies?: string;
	qplayerPlaylistInitPosition?: string;
	qplayerSubtitlesAuto?: string;
	qplayerAspectRatio?: string;
	qplayerAutoSizing?: string;
	qplayerPreview?: string;
	qplayerModalInstance?: string;
	qplayerTopRightSpace?: string;

	qplayerMobileDevice?: string;
	qplayerDev?: string;
	qplayerBeta?: string;
	qplayerAlpha?: string;

	// Media details related params
	qplayerMediaTitle?: string;
	qplayerMediaDescription?: string;

	// Form related params
	qplayerEventLink?: string;
	qplayerFormPolicyUrl?: string;
	qplayerFormTitle?: string;
	qplayerFormDescription?: string;
	qplayerFormCoverUrl?: string;
	qplayerFormPrimaryColor?: string;
	qplayerFormToken?: string;
	qplayerBonnierDi?: string;
	qplayerFiles?: string;

	containerKey?: string;
	container?: HTMLElement;
}

const App = (props: AppProps) => {
	// Context hooks
	const {
		adsPlaying,
		currentPosition,
		showShoppingCart,
		showChatWindow,
		isPlaying,
		duration,
		isSticky,
		setCurrentSceneWidgets,
		setPlayerCurrentPosition,
		setIsPlaying,
		setIsSticky,
		setShowChatWindow,
		setShowShoppingCart,
	} = usePlayer();

	const {
		restrictedMedias,
		relatedMediasOfScene,
		screenType,
		mediaType,
		scenario,
		sceneConfig,
		currentMediaId,
		playlistId,
		mediaName,
		accountId,
		isMediaLive,
		currentConfigId,
		mediasData,
		restrictedToken,
		viewerEngagementModuleActivated,
		openSettingMenu,
		showTranscriptionsModule,
		playerWidth,
		configParams,
		isFullScreen,
		showRegistrationForm,
		setCurrentMediaId,
		setEmbedShareSettings,
		setMediasData,
		setOpenSettingMenu,
		setSceneConfig,
		setScreenType,
		setMinimizedPlayerState,
		setConfigParams,
	} = useConfiguration();

	const { unlockedMedias } = useRestriction();
	const { presentationPublishedSlides } = usePresenter();

	const countdownRef = useRef<any | null>(null);
	const playerHoverTimeoutId = useRef<any | null>(null);
	const resetPlayerHoverRef = useRef<boolean | null>(null);
	const androidInteractOverlayPositionUpdated = useRef(false);
	const languageSettingMenuRef = useRef<any | null>(null);
	const playerWindowRef = useRef<HTMLDivElement | null>(null);

	const [loading, setLoading] = useState(true);
	const [sideWindowOpen, setSideWindowOpen] = useState(false);
	const [playerHover, setPlayerHover] = useState(false);
	const [tokens, setTokens] = useState<string[] | null>(null); // Initial tokens state has to be null
	const [hideRightControls, setHideRightControls] = useState(true);
	const [analyticsCloseEvent, setAnalyticsCloseEvent] = useState(false);

	const container = props.container ?? document;
	const isInlinePlayer = useMemo(() => configParams?.inline !== 'off', [configParams]);
	const isLoadedByLoader = useMemo(() => configParams?.useLoader === 'true', [configParams]);
	const fromSceneEditor = useMemo(() => configParams?.fromSceneEditor === 'true', [configParams]);
	const isUsedOnMobile = useMemo(() => isUsedOnMobileFunc(configParams), [configParams]);
	const screenBreakpointClass = useMemo(() => {
		if (isUsedOnMobile) {
			return '';
		}
		let screenTypeClass = "";
		switch (screenType) {
			case SCREEN_TYPE.mobile:
				screenTypeClass = isUsedOnMobile ? 'qplayer-device-mobile' : 'qplayer-small-screen';
				break;
			case SCREEN_TYPE.small:
				screenTypeClass = 'qplayer-small-screen';
				break;
			case SCREEN_TYPE.medium:
				screenTypeClass =  'qplayer-medium-screen';
				break;
			case SCREEN_TYPE.big:
				screenTypeClass = 'qplayer-big-screen';
				break;
			case SCREEN_TYPE.huge:
				screenTypeClass = 'qplayer-huge-screen';
				break;
			default:
				screenTypeClass = '';
		}
		return screenTypeClass;
	}, [isUsedOnMobile, screenType]);

	const isStoryPlaylist = playlistId && typeof configParams?.configId === "string" && configParams.configId.toLowerCase().includes(STORY_CONFIG_ID.toLowerCase());
	const currentPlayerId = `player-${isStoryPlaylist ? currentMediaId : (playlistId || currentMediaId)}-${configParams.containerKey}`;
	// Replaces the story playlist's own slider. Both halves of the rule live in the helper.
	const isReelsFeed = isReelsFeedEnabled(configParams);

	const getRestrictToken = useCallback((mediaId?: string, isScenario = false) => getMediaRestrictToken({ mediaId, isScenario, tokens: tokens ?? [], restrictedToken, scenario: scenario, unlockedMedias }), [restrictedToken, scenario, tokens, unlockedMedias]);

	const setLoadingCallback = useCallback((value: boolean) => {
		setLoading(value);
	}, []);
	const updateAndroidInteractOverlayPositionCallback = useCallback(() => (androidInteractOverlayPositionUpdated.current = false), []);
	const getScenarioTokenCallback = useCallback(() => getRestrictToken(undefined, true), [getRestrictToken]);

	const { playerMobileConfig, playerDesktopConfig } = useFetchPlayerConfigData({
		playerConfigId: configParams?.configId as string,
		playerConfigURL: configParams?.presetUrl as string,
		accountId: configParams?.accountId as string,
		fromSceneEditor,
		configParams: configParams,
		isMediaLive,
		setLoading: setLoadingCallback,
	});

	useEffect(() => {
		if (!(configParams.containerKey || "").includes("-widget")) {
			return;
		}
		const playerConfig = isUsedOnMobile ? playerMobileConfig : playerDesktopConfig;

		const playerPrimaryColor = playerConfig?.playerStyling ?.primaryColor ?? "#126cfc";
		const widgetContainer = document.querySelector(`qplayer-widget-container[data-qplayer-widget-container-key="${configParams.containerKey}-container"]`) as HTMLElement;
		if (widgetContainer) {
			widgetContainer.style.borderColor = `${playerPrimaryColor}`;
			widgetContainer.style.backgroundColor = playerPrimaryColor;
			const playCornerIcon = widgetContainer.querySelector('.qplayer__play-corner-icon') as HTMLElement;
			playCornerIcon.style.backgroundColor = playerPrimaryColor;
		}
	}, [configParams.containerKey, isUsedOnMobile, playerDesktopConfig, playerMobileConfig]);

	useProcessMediaData({
		playlistId: configParams?.playlistId as string,
		mediaId: configParams?.mediaId as string,
		accountId: configParams?.accountId as string,
	});
	useFetchInteractProjectData({
		tokens,
		scenarioId: configParams?.scenarioId as string,
		accountId: configParams?.accountId as string,
		isUsedOnMobile,
		fromSceneEditor,
		setLoading: setLoadingCallback,
		updateAndroidInteractOverlayPosition: updateAndroidInteractOverlayPositionCallback,
		getRestrictToken: getScenarioTokenCallback,
		configParams: configParams,
	});
	useFetchTranscriptData({
		showTranscriptSettings: (isUsedOnMobile ? playerMobileConfig : playerDesktopConfig)?.bottomControls?.showTranscriptSettings,
	});
	useGoogleAnalytic({
		scenario,
		playerDesktopConfig,
		playerMobileConfig,
		configParams: configParams,
		currentMediaId,
		sceneConfig,
		isPlaying: !!isPlaying,
		currentPosition: typeof currentPosition === 'string' ? 0 : currentPosition,
		mediaName,
		accountId: configParams?.accountId,
	});

	const activePlayerConfig = isUsedOnMobile ? playerMobileConfig : playerDesktopConfig;
	const stickyEnabled =
		isInlinePlayer &&
		!configParams?.scenarioId &&
		!!activePlayerConfig?.additionalSettings?.stickyVideoPlayer;
	const { dismissStickyPlayer } = useStickyVideoPlayer({
		container: configParams?.container,
		enabled: stickyEnabled,
		isPlaying: !!isPlaying,
		isFullScreen,
		isSticky,
		currentPlayerId,
		currentPosition,
		isMediaLive,
		setIsSticky,
		onBeforeSticky: () => {
			setShowChatWindow(false);
			setShowShoppingCart(false);
		},
		mediasData,
		currentMediaId,
		stickyVideoPlayerSize: activePlayerConfig?.additionalSettings?.stickyVideoPlayerSize,
		isUsedOnMobile,
	});

	const storyPlaylistInitPosition = configParams?.initPosition ? +configParams?.initPosition : 0;
	// Swiper reports every slide inactive until it has initialised, and a Player creates its GoBrain
	// widget on mount - so every Story slide used to be created with `autoplay` off (player-helper
	// disables it for a slide that is not visible), and the opening clip had to be poked into
	// playing by a flat one-second timer. That timer is the black screen at the start of a Story
	// embed. Until Swiper reports for itself, the opening slide is the visible one.
	const [hasStorySwiperInitialised, setHasStorySwiperInitialised] = useState(false);
	// Only count menus/dialogs that are actually open: the mobile language/quality <dialog>s stay mounted while closed
	// (no `open` attribute, aria-hidden="true") and would otherwise block the hide forever.
	const hasOpenMenu = () =>
		Array.from(
			container.querySelectorAll('.qplayer__language-container,.qplayer__setting-menu,.qplayer__subtitle-setting,.qplayer__sharing-dialog')
		).some((menu) => !(menu instanceof HTMLDialogElement && !menu.open) && menu.getAttribute('aria-hidden') !== 'true');

	const handleHiddenControlBar = () => {
		if (playerHoverTimeoutId.current) {
			clearTimeout(playerHoverTimeoutId.current);
		}
		if (isPlaying === false) {
			return;
		}

		playerHoverTimeoutId.current = setTimeout(() => {
			if (resetPlayerHoverRef.current === true || !resetPlayerHoverRef) {
				return;
			}
			// Keep the controls visible while a menu/dialog is open.
			if (hasOpenMenu()) {
				return;
			}

			setPlayerHover(false);
		}, 3000);
	};

	const closeModal = useCallback(() => {
		if (configParams?.container.getAttribute('data-qplayer-modal-open') !== "true") {
			return;
		}

		configParams.container && configParams.container.setAttribute('data-qplayer-modal-open', 'false');
		currentPlayerId && (window as any).GoBrain.widgets(currentPlayerId)?.pause?.();
		currentPlayerId && (window as any).GoBrain.widgets(currentPlayerId)?.position?.(0);
		setPlayerCurrentPosition(0);
		setIsPlaying(undefined);
		if (sceneConfig.currentScene === scenario?.defaults?.sceneId || !scenario?.defaults?.sceneId) {
			return;
		}
		setSceneConfig({ currentScene: scenario?.defaults?.sceneId });
	}, [configParams.container, currentPlayerId, scenario?.defaults?.sceneId, sceneConfig.currentScene, setIsPlaying, setPlayerCurrentPosition, setSceneConfig]);

	const handleModalContainerClickEvent = useCallback((e) => {
		if (e.target.classList.contains('play-button') || e.target.closest('.play-button')) {
			currentPlayerId && (window as any).GoBrain.widgets(currentPlayerId)?.position?.(0);
			setPlayerCurrentPosition(0);
			setIsPlaying(undefined);

			setTimeout(() => {
				currentPlayerId && (window as any).GoBrain.widgets(currentPlayerId)?.play?.();
			}, 100);
		}
	}, [currentPlayerId, setIsPlaying, setPlayerCurrentPosition]);

	const handleModalContainerKeydownEvent = useCallback((e) => {
		if (e.key === 'Enter' || e.key === ' ') {
			handleModalContainerClickEvent(e);
		}
	}, [handleModalContainerClickEvent]);

	const handlePlayerContainerClickEvent = useCallback((e) => {
		if (!configParams.isModalInstance) {
			return;
		}

		if (configParams.container && e.target.classList.contains('qplayer-modal-player-container')) {
			closeModal();
		}
	}, [closeModal, configParams.container, configParams.isModalInstance]);

	const handleQplayerAppClick = useCallback(() => {
		if (!(configParams.containerKey || "").includes("-widget")) {
			return;
		}
		const widgetContainer = document.querySelector(`qplayer-widget-container[data-qplayer-widget-container-key="${configParams.containerKey}-container"]`) as HTMLElement;
		if (!widgetContainer) {
			return;
		}

		widgetContainer.className = isUsedOnMobile ? "qplayer-widget-embed-instance-maximized qplayer-widget-embed-instance-mobile" : "qplayer-widget-embed-instance-maximized";

		const widgetRef = document.querySelector(`qplayer-embed[data-qplayer-key="${configParams.widgetKey}"]`);
		if (widgetRef) {
			widgetRef.className = "";
		}
		const widgets = (window as any).GoBrain.widgets()[currentPlayerId];
		widgets?.play?.();
	}, [configParams.containerKey, configParams.widgetKey, currentPlayerId, isUsedOnMobile]);

	const handlePlayerHover = useCallback((event?: any) => {
		// Ignore hover events whose target is outside this player's .playerWindow (e.g. side windows, chat, transcriptions).
		const target = event?.target as Node | null | undefined;
		if (target && !playerWindowRef.current?.contains(target)) {
			return;
		}
		setPlayerHover(true);
		handleHiddenControlBar();
	}, [isPlaying]);

	const renderSceneEditorPlayer = useCallback(() => {
		return currentMediaId && currentMediaId !== '' && configParams?.accountId !== '' && playerDesktopConfig !== null ? (
			<Player
				key={EDITOR_PLAYER_ID + currentMediaId}
				accountId={configParams?.accountId as string}
				isVisible
				playerMedia={currentMediaId}
				settings={playerDesktopConfig}
				configParams={{}}
				layout={'desktop'}
				playerId={EDITOR_PLAYER_ID}
			/>
		) : null;
	}, [configParams?.accountId, currentMediaId, playerDesktopConfig]);

	const closeSettingsMenus = useCallback(() => {
		setOpenSettingMenu(null);
	}, [setOpenSettingMenu]);

	const executeDataFromEditor = (editorData: any) => {
		const editingScene = editorData.scene;
		const activeLayout = editorData.activeLayout;

		if (!editingScene) {
			return;
		}

		let mediasToLoad: any[] = [];
		// GET ALL MEDIA IN SCENE
		let layout = editingScene?.layouts?.find((l: any) => l.type === activeLayout);
		layout = layout ?? editingScene?.layouts?.[0];
		const boxes = layout?.boxes ?? [];
		let mediaData = null;
		let start = 0;
		let videoLength = 0;

		const endOfScene = (editingScene.events ?? [])
			.find((e: any) => e.type === 'endOfScene')
			?.actions?.find((action: any) => ['goToScene', 'loopCurrentScene', 'goToHome'].includes(action.type));

		if (boxes.length > 0) {
			let widgets: any[] = [];
			boxes.forEach((box: any) => {
				box.widgets?.forEach((w: any) => widgets.push(w));
			});
			editingScene.widgetTemplates.forEach((tmp: any) => {
				const currentBoxWidget = widgets.find((w) => w.widgetTemplateId === tmp.id);
				if (!currentBoxWidget) {
					return;
				}
				if (tmp.type === 'video' && !mediasToLoad.find((m) => m.id === tmp.settings?.mediaId)) {
					start = currentBoxWidget.start && moment(getTimelineTimeFromTimeInput(currentBoxWidget.start));
					const end = currentBoxWidget.end && moment(getTimelineTimeFromTimeInput(currentBoxWidget.end));
					videoLength = getSecondsFromTimelineTime(end) - getSecondsFromTimelineTime(start) + 1;
					mediaData = {
						id: tmp.settings?.mediaId,
						start: getSecondsFromTimelineTime(start),
						end: getSecondsFromTimelineTime(end),
						videoLength: videoLength,
						isMediaLive: false,
						mediaName: tmp.name,
						sceneId: editingScene.id,
					};
					mediasToLoad.push({ id: tmp.settings?.mediaId, sceneId: editingScene.id, endOfScene: endOfScene });
				}
			});
		}

		setCurrentMediaId(mediasToLoad[0].id);
		mediasToLoad = Array.from(new Set(mediasToLoad));
		setMediasData([mediaData]);
		setCurrentSceneWidgets(null);
		setSceneConfig({
			currentScene: editingScene.id,
			nextScene: editingScene.id,
		});
	};

	const handleKeyup = (event: KeyboardEvent) => {
		if (event.key === 'Tab') {
			resetPlayerHoverRef.current = true;
			setPlayerHover(true);

			if (openSettingMenu) {
				const isCloseableByTabMenu =
					!!(event.target as any)?.classList.contains('closeable-by-tab') ||
					!!(event.target as any)?.children?.[0]?.classList.contains('closeable-by-tab');

				if (isCloseableByTabMenu) {
					setOpenSettingMenu(undefined);
				} else if ((event.target as any).tabIndex === -1) {
					// Avoid redundant tab
					const elements = (event.target as any).querySelectorAll('li');

					if (elements.length > 0) {
						event.shiftKey ? elements[elements.length - 1].focus() : elements[0].focus();
					}
				}
			}
			handleHiddenControlBar();
		}

		if (event.key === 'Enter') {
			resetPlayerHoverRef.current = true;
			setPlayerHover(true);
			handleHiddenControlBar();
			return;
		}

		resetPlayerHoverRef.current = false;
		handleHiddenControlBar();
	};

	const handleOnMessage = useCallback(
		(event) => {
			if (event.data?.type === 'qplayer_tokens' && event.data?.tokens) {
				setTokens(event.data?.tokens ?? []);
			}
			if (event.data?.type === 'qplayer-embed-origin') {
				setEmbedShareSettings(event.data);
			}

			if (event.data?.type === 'updateScene') {
				executeDataFromEditor(event.data);
			}

			const widgets = (window as any).GoBrain.widgets()[`player-${playlistId || currentMediaId}-${configParams?.containerKey}`];

			// Control the player from the loader
			if (event.data && event.data.identifier === MessageHelper.MESSAGE_BASE_IDENTIFIERS.loaderIdentifier) {
				if (event.data.command === 'play') {
					if (
						(container.querySelector('div[data-internal-gobrain-box-configuration-modulename="Covers"]') as any)?.style.display !==
						'block'
					) {
						widgets?.play?.();
					}
				} else if (event.data.command === 'pause' || event.data.command?.data === 'pause') {
					widgets?.pause?.();
				} else if (event.data.command === 'play-minimized') {
					widgets?.play?.();
				} else if (event.data.command === 'maximize') {
					setHideRightControls(false);
				} else if (event.data.command === 'close-modal') {
					widgets?.pause();
					setHideRightControls(false);
					setAnalyticsCloseEvent(true);
				} else if (event.data.command === 'minimize') {
					setHideRightControls(true);
				}

				if (event.data?.command === MessageHelper.MESSAGE_TYPES.minimizePlayer) {
					widgets?.pause?.();
					setMinimizedPlayerState(true);
				}
				if (event.data?.command === MessageHelper.MESSAGE_TYPES.maximizePlayer) {
					typeof event?.data?.position === 'number' && widgets?.position?.(event.data.position);
					typeof event?.data?.volume === 'number' && widgets?.volume?.(event.data.volume);
					widgets?.play?.();
					setMinimizedPlayerState(false);
				}
			}

			// Alow customers to control the player from the parent window
			if (event.data && event.data.identifier === MessageHelper.MESSAGE_BASE_IDENTIFIERS.embeddedIdentifier) {
				if (event.data.command === MessageHelper.MESSAGE_TYPES.play) {
					widgets?.play?.();
				}
				if (event.data.command === MessageHelper.MESSAGE_TYPES.pause) {
					widgets?.pause?.();
				}
				if (event.data.command === MessageHelper.MESSAGE_TYPES.position) {
					widgets?.position?.(event.data?.metadata?.position);
				}
				if (event.data.command === MessageHelper.MESSAGE_TYPES.volume) {
					widgets?.volume?.(event.data?.metadata?.volume);
				}
			}

			if (event.data && (event.data.command?.data ?? '').includes('play')) {
				setHideRightControls(false);
			}
		},
		[configParams?.containerKey, configParams?.qplayerId, container, currentMediaId, playlistId]
	);

	const changePlayerSize = useCallback((playerWidth) => {
		if (isUsedOnMobile) {
			return;
		}
		const containerWidth = isFullScreen || configParams.qplayerPreview === "true" ? window.innerWidth : (container as any)?.getBoundingClientRect()?.width || window.innerWidth;
		const width = configParams.qplayerPreview === "true" ? containerWidth : containerWidth / 100 * playerWidth;
		countdownRef.current?.changeLimitedTitleLines(width < 700 ? 3 : 4);

		let newScreenType = SCREEN_TYPE.huge;
		if (width <= 430) {
			newScreenType = SCREEN_TYPE.mobile;
		} else if (width <= 600) {
			newScreenType = SCREEN_TYPE.small;
		} else if (width <= 768) {
			newScreenType = SCREEN_TYPE.medium;
		} else if (width <= 1280) {
			newScreenType = SCREEN_TYPE.big;
		}
		if (screenType !== newScreenType) {
			setScreenType(newScreenType);
		}
	}, [isUsedOnMobile, screenType, setScreenType, container, isFullScreen]);

	const changeScreenSize = useCallback(() => {
		// In the slides layout the video is one column of a two-column grid, so the embed is about
		// twice as wide as the player. Measured on the embed, the small breakpoints - and with them
		// the compact control bar - never trigger. `.qplayer-presenter` is only on the element when
		// that layout is active; the passthrough variant deliberately does not match it.
		const presenterPlayerWidth = playerWindowRef.current?.closest('.qplayer-presenter')
			? playerWindowRef.current.getBoundingClientRect().width
			: 0;
		const width = isFullScreen
			? window.innerWidth
			: presenterPlayerWidth || (container as any)?.getBoundingClientRect()?.width || window.innerWidth;
		countdownRef.current?.changeLimitedTitleLines(width < 700 ? 3 : 4);

		if (isUsedOnMobile) {
			return;
		}

		let newScreenType = SCREEN_TYPE.huge;
		if (width <= 430) {
			newScreenType = SCREEN_TYPE.mobile;
		} else if (width <= 600) {
			newScreenType = SCREEN_TYPE.small;
		} else if (width <= 768) {
			newScreenType = SCREEN_TYPE.medium;
		} else if (width <= 1280) {
			newScreenType = SCREEN_TYPE.big;
		}
		if (screenType !== newScreenType) {
			setScreenType(newScreenType);
		}
	}, [isFullScreen, container, isUsedOnMobile, screenType, setScreenType]);

	useEffect(() => {
		const params = getConfigParams({ props, queryParams: queryString.parse(window.location.search) })
		if (params.accountId !== configParams.accountId) {
			setConfigParams(params);
		}
	}, [props, configParams]);

	// With autoSizing the embed follows the current video's own ratio instead of the configured aspectRatio.
	useEffect(() => {
		const autoSizing = configParams.autoSizing !== undefined ? configParams.autoSizing : activePlayerConfig?.additionalSettings?.autoSizing;
		if (!autoSizing) {
			return;
		}
		const currentMedia = mediasData.find(({ id, originalId }: any) => id === currentMediaId || originalId === currentMediaId);
		if (!currentMedia?.videoWidth || !currentMedia?.videoHeight) {
			return;
		}
		const videoAspectRatio = `${currentMedia.videoWidth}/${currentMedia.videoHeight}`;
		if (configParams.aspectRatio !== videoAspectRatio) {
			setConfigParams({ ...configParams, aspectRatio: videoAspectRatio });
		}
	}, [configParams, mediasData, currentMediaId]);

	useEffect(() => {
		setTokens(configParams.token ? [configParams.token] : []);
	}, [configParams.token]);

	useEffect(() => {
		if (showRegistrationForm) {
			return;
		}
		const hasPresentationSlides = presentationPublishedSlides && presentationPublishedSlides.length > 0;
		if (hasPresentationSlides) {
			return;
		}
		// changePlayerSize(playerWidth);
	}, [changePlayerSize, playerWidth, showRegistrationForm, isFullScreen]);

	// The slides layout resizes the player without resizing the window, so its width has to be
	// watched on the element itself - the resize listener below never fires for it.
	useEffect(() => {
		const playerWindow = playerWindowRef.current;
		if (!playerWindow) {
			return;
		}
		const observer = new ResizeObserver(() => changeScreenSize());
		observer.observe(playerWindow);
		return () => observer.disconnect();
	}, [changeScreenSize, loading, presentationPublishedSlides]);

	useEffect(() => {
		changeScreenSize();

		window.addEventListener('message', handleOnMessage);
		window.addEventListener('resize', changeScreenSize);
		props.container && props.container.addEventListener('click', handlePlayerContainerClickEvent);
		const modalContainer = document.querySelector('qplayer-modal-embed[data-qplayer-modal-embed-key="' + (configParams.containerKey || "") + '"]');
		modalContainer && modalContainer.addEventListener('click', handleModalContainerClickEvent);
		modalContainer && modalContainer.addEventListener('keyup', handleModalContainerKeydownEvent);

		return () => {
			window.removeEventListener('message', handleOnMessage);
			window.removeEventListener('resize', changeScreenSize);
			props.container && props.container.removeEventListener('click', handlePlayerContainerClickEvent);
			const modalContainer = document.querySelector('qplayer-modal-embed[data-qplayer-modal-embed-key="' + (configParams.containerKey || "") + '"]');
			modalContainer && modalContainer.removeEventListener('click', handleModalContainerClickEvent);
			modalContainer && modalContainer.removeEventListener('keyup', handleModalContainerKeydownEvent);
		}
	}, [changeScreenSize, configParams.containerKey, handleModalContainerClickEvent, handleModalContainerKeydownEvent, handleOnMessage, handlePlayerContainerClickEvent, props.container]);

	// need to move this event out cause handleKeyup need to get the openSettingMenu value
	useEffect(() => {
		window.addEventListener('keyup', handleKeyup);
		return () => {
			window.removeEventListener('keyup', handleKeyup);
		};
	}, [openSettingMenu]);

	useEffect(() => {
		const isUnder750 = isFullScreen || configParams.qplayerPreview === "true" || configParams.isModalInstance ? false : (configParams.container?.getBoundingClientRect()?.width || window.innerWidth) <= 750;
		setSideWindowOpen(
			isUsedOnMobile ? false : showShoppingCart || (viewerEngagementModuleActivated && showChatWindow) || (showTranscriptionsModule && !isUnder750)
		);
	}, [showShoppingCart, showChatWindow, viewerEngagementModuleActivated, isUsedOnMobile, showTranscriptionsModule, isFullScreen, configParams.qplayerPreview, configParams.container, configParams.isModalInstance]);

	useEffect(() => {
		if (isPlaying) {
			const currentActive = document.activeElement;
			if (!currentActive || currentActive.closest('.controlsOverlay') || currentActive.closest('.chat-container')) {
				return;
			}

			const controlsOverlay = container.getElementsByClassName('controlsOverlay')[0] as any;
			if (!controlsOverlay) {
				return;
			}
			if (!isInlinePlayer) {
				controlsOverlay.focus();
			}

			handlePlayerHover();
		} else {
			setPlayerHover(true);
		}

		if (isPlaying === undefined) {
			setSideWindowOpen(false);
		}
	}, [isPlaying, isInlinePlayer, handlePlayerHover, container]);

	useEffect(() => {
		if (analyticsCloseEvent) {
			const currentMedia = mediasData.find(({ id, originalId }: any) => id === currentMediaId || originalId === currentMediaId);
			window.parent.postMessage(
				{
					origin: 'Qbrick.Interact.Widget.Element',
					identifier: 'qbrick-player-analytics-message',
					command: {
						type: 'AnalyticsEvent',
						action: 'click',
						data: 'closePlayer',
						mediaName: mediaName,
						mediaId: currentMediaId,
						accountId: accountId,
						videoLength: currentMedia?.duration ?? duration,
						isMediaLive: isMediaLive,
						currentPosition: isMediaLive ? Date.now() : currentPosition,
					},
				},
				'*'
			);
			setAnalyticsCloseEvent(false);
		}
	}, [
		currentMediaId,
		accountId,
		currentConfigId,
		playerDesktopConfig,
		playerMobileConfig,
		analyticsCloseEvent,
		mediaName,
		isMediaLive,
		currentPosition,
		duration,
		mediasData,
	]);

	const checkSubtitleBottomMargin = useCallback(() => {
		const bottomControlBar = container.querySelector('div.bottomControlbarHover > .bottomControlbar');
		const hoverMarginBottom = bottomControlBar?.getBoundingClientRect()?.height;
		const subtitleModule = container.querySelector('div[data-internal-gobrain-box-configuration-modulename=Subtitles]') as any;
		const playerContainer = container.querySelector(`#${currentPlayerId}-container`);

		if (playerContainer && playerContainer.classList.contains('qplayer-overlay--pip')) {
			if (subtitleModule) {
				// No need to set bottom space for subtitle container since the control bar is hidden
				subtitleModule.style.marginBottom = '0px';
			}
			return;
		}

		if (subtitleModule) {
			const marginBottom = playerHover && hoverMarginBottom === undefined ? 60 : hoverMarginBottom ?? 0;
			subtitleModule.style.marginBottom = marginBottom + 'px';
		}
	}, [playerHover, container, currentPlayerId]);
	useEffect(checkSubtitleBottomMargin, [checkSubtitleBottomMargin]);

	useEffect(() => {
		if (!playerHover && !isUsedOnMobile && openSettingMenu) {
			setOpenSettingMenu(undefined);
		}
	}, [playerHover, isUsedOnMobile]);

	const shouldShowPasswordScreen = restrictedMedias.includes(currentMediaId) || restrictedMedias.includes("scenario");
	const mobileClassName = isUsedOnMobile || configParams?.fromQplayerWidget === "true" ? 'qplayer-app--mobile' : '';
	const isUnder750 = isFullScreen || configParams.qplayerPreview === "true" || configParams.isModalInstance ? false : (configParams.container?.getBoundingClientRect()?.width || window.innerWidth) <= 750;

	const renderPlayers = useCallback(
		(showPasswordScreen = false) => {
			const config = isUsedOnMobile ? playerMobileConfig : playerDesktopConfig;
			if (!configParams?.accountId || (!currentMediaId && !playlistId) || !config || (tokens === null && configParams?.useLoader === 'true')) {
				console.warn('Player is not ready yet, please check the accountId, mediaId/playlistId, config and tokens');
				return null;
			}

			const showControls = configParams?.noControls !== 'true' && !showPasswordScreen;

			const currentMedia = mediasData.find(({ id, originalId }: any) => id === currentMediaId || originalId === currentMediaId);
			// A Story slide mounts a GoBrain widget of its own, so a long playlist used to create every one
			// of them up front: 47 clips meant 47 players, ~890 requests and ten seconds before the first
			// frame, and a hundred clips never started at all. Only the active slide and its neighbours are
			// mounted now, which is what the reels feed has done since the start. Derived from
			// currentMediaId rather than kept as state of its own - onInit and onActiveIndexChange both set
			// it already, and it is in this callback's dependencies.
			const storyActiveMediaIndex = mediasData.findIndex((media: any) => media?.id === currentMediaId);
			const storyActiveIndex = storyActiveMediaIndex === -1 ? storyPlaylistInitPosition : storyActiveMediaIndex;
			// Keep the configured aspect ratio for on-demand media. The autoplay + presentation branch used to drop it, but the playerWindow relies on aspectRatio for its height, so removing it once the presenter auto-opened collapsed the layout.
			const shouldSetAspectRatio = currentMedia?.live || isMediaLive ? (configParams?.qplayerPreview ? false : true) : true;

			// The presenter module and the side windows shrink the playerWindow to a fraction of the embed width.
			const playerWindowWidth = isUsedOnMobile ? 100 : sideWindowOpen ? isUnder750 ? 100 : 75 : 100;
			// Since the playerWindow is the in-flow element that carries the aspect ratio, `aspect-ratio` derives its
			// height from that shrunken width, so the window ends up shorter than the embed and the control bar - which
			// is pinned to the bottom of the playerWindow through .controlsOverlay - rides up to the video's bottom edge.
			// Scaling the ratio by the same fraction keeps the height equal to a full-width box, so the control bar stays
			// on the bottom of the embed next to the presenter module.
			const aspectRatioValue = getAspectRatioValue(configParams?.aspectRatio);
			const playerWindowAspectRatio = aspectRatioValue ? `${(aspectRatioValue * playerWindowWidth) / 100}` : configParams?.aspectRatio;

			return (
				<Presenter settings={config}>
					<div
						ref={playerWindowRef}
						className={joinClassNames('playerWindow')}
						style={{
							...(
								isUsedOnMobile
									? { width: '100%', height: '100%' }
									: { width: `${playerWindowWidth}%` }
							),
							...(!(isUsedOnMobile && configParams.isModalInstance) && shouldSetAspectRatio ? configParams?.aspectRatio ? { aspectRatio: playerWindowAspectRatio, position: "relative" } : {} : {}),
							...(showRegistrationForm === false ? {} : !configParams.qplayerPreview ? { display: 'none', opacity: 0 } : {}), // Check false here to avoid the case of showRegistrationForm is undefined at the beginning, which will cause the player to flash before hiding when showRegistrationForm is set to false later
						}}
					>
						{!shouldShowPasswordScreen && !fromSceneEditor && !isSticky && <>
							<QualitySettingMenu
								className={screenBreakpointClass}
								sideWindowOpen={isUsedOnMobile ? false : sideWindowOpen}
								playerConfigs={isUsedOnMobile ? playerMobileConfig : playerDesktopConfig}
								isMobile={isUsedOnMobile || screenType === SCREEN_TYPE.small || screenType === SCREEN_TYPE.mobile}
							/>
							<LanguageSettingMenu
								className={screenBreakpointClass}
								sideWindowOpen={isUsedOnMobile ? false : sideWindowOpen}
								playerConfigs={isUsedOnMobile ? playerMobileConfig : playerDesktopConfig}
								isMobile={isUsedOnMobile || screenType === SCREEN_TYPE.small || screenType === SCREEN_TYPE.mobile}
								onLanguageChange={(type, _code) => type === "subtitle" && checkSubtitleBottomMargin()}
							/>
							<SubtitleSettingMenu languageSettingMenuRef={languageSettingMenuRef} sideWindowOpen={sideWindowOpen} />
						</>}
						{mediaType === 'scenario' &&
							mediasData.map((media: any) => {
								const mediaId = media.id;
								// The same media can be listed once per scene that uses it, but only one player per media
								// is rendered: the current scene's entry, otherwise the first one.
								const currentSceneEntry = mediasData.find((m: any) => m.id === mediaId && m.sceneId === sceneConfig.currentScene);
								if (media !== (currentSceneEntry ?? mediasData.find((m: any) => m.id === mediaId))) {
									return null;
								}
								const endOfScene = media.endOfScene;
								const relatedMediaData = sceneConfig.currentScene ? relatedMediasOfScene[sceneConfig.currentScene] ?? [] : [];
								const isNeededToInit = relatedMediaData.find((id: string) => id === mediaId);

								if (currentMediaId === mediaId || (!isIOS && isNeededToInit)) {
									return (
										<Player
											key={`${mediaId}-${configParams.containerKey}`}
											accountId={configParams?.accountId as string}
											isVisible={currentMediaId === mediaId}
											playerMedia={mediaId}
											settings={config}
											layout={isUsedOnMobile ? 'mobile' : 'desktop'}
											isMaintainProportions={isUsedOnMobile ? config?.playbackOptions?.maintainProportions : false}
											playerId={`player-${mediaId}-${configParams.containerKey}`}
											endOfScene={endOfScene}
											token={getRestrictToken(mediaId)}
											handlePlayerHover={handlePlayerHover}
											container={props.container}
											mediaData={media}
										/>
									);
								}
							})}

						{mediaType !== 'scenario' && !isStoryPlaylist && (
							<Player
								accountId={configParams?.accountId as string}
								isVisible={true}
								playerMedia={playlistId || currentMediaId}
								settings={config}
								configParams={configParams}
								layout={isUsedOnMobile ? 'mobile' : 'desktop'}
								isMaintainProportions={isUsedOnMobile ? config?.playbackOptions?.maintainProportions : false}
								playerId={`player-${playlistId || currentMediaId}-${configParams.containerKey}`}
								token={getRestrictToken(currentMediaId ?? playlistId)}
								handlePlayerHover={handlePlayerHover}
								container={props.container}
								mediaData={currentMedia}
							/>
						)}

						{isReelsFeed && mediasData?.length > 0 && (
							<StoryFeed
								settings={config}
								container={props.container}
								isUsedOnMobile={isUsedOnMobile}
								getRestrictToken={getRestrictToken}
								handlePlayerHover={handlePlayerHover}
							/>
						)}

						{isStoryPlaylist && !isReelsFeed && mediasData?.length > 0 && (
							<Swiper
								// Swiper's own pointer engine rather than cssMode, for the same reason StoryFeed
								// uses it. cssMode hands scrolling to native scroll-snap, and the control bar is a
								// sibling of this swiper rather than a child, so a wheel over the controls never
								// reached the scroll container and the feed did not move at all. It also makes
								// simulateTouch mean something: cssMode ignores it, so mouse drag never worked.
								navigation
								simulateTouch
								// Up and down, like the reels feed and like every other short-form feed. Swiper's
								// default is horizontal, so this swiper used to scroll sideways while the same
								// playlist opened in a reel scrolled vertically.
								direction="vertical"
								// A vertical swiper needs a bounded height or the wrapper just grows to the sum of
								// its slides. The slides are already 100vh each, which is the height they were
								// drawn against all along.
								style={{ height: '100vh' }}
								followFinger
								threshold={5}
								speed={380}
								slidesPerView={'auto'}
								spaceBetween={10}
								// Swiper's engine moves by transform, so the wheel only works through this module.
								// thresholdDelta swallows trackpad jitter; a gesture still advances a single slide
								// however hard it is thrown. The one dead spot is the centre play disc, which
								// consumes the event before it reaches the swiper.
								mousewheel={{ forceToAxis: true, thresholdDelta: 6 }}
								modules={[Mousewheel, Navigation]}
								onInit={() => {
									// The opening clip autostarts because its player was created visible, so there
									// is nothing to poke here. This used to wait a flat second and then call play().
									setCurrentMediaId(mediasData[storyPlaylistInitPosition].id);
									setHasStorySwiperInitialised(true);
								}}
								initialSlide={storyPlaylistInitPosition}
								onActiveIndexChange={(swiper: any) => {
									(window as any).GoBrain.widgets(`player-${currentMediaId}-${configParams.containerKey}`)?.pause?.();
									const nextActivePlayerId = mediasData[swiper.activeIndex].id;
									(window as any).GoBrain.widgets(`player-${nextActivePlayerId}-${configParams.containerKey}`)?.play?.();
									setCurrentMediaId(nextActivePlayerId);
								}}
							>
								{mediasData.map((media: any, _index: number) => {
									const mediaId = media.id;
									const thumbnail = getStoryThumbnailUrl(media);
									// Further away than the window, the slide stays the poster it already paints.
									const isMounted = Math.abs(_index - storyActiveIndex) <= STORY_SLIDE_WINDOW;
									return (
										<SwiperSlide
											key={`${mediaId}-${configParams.containerKey}`}
											style={{
												scrollSnapStop: 'always',
											}}
										>
											{({ isActive }) => {
												return (
													<div
														style={{
															width: '100vw',
															height: '100vh',
															overflow: 'hidden',
															position: 'relative',
															background: `url(${thumbnail}) center / cover no-repeat`,
														}}
													>
														{isMounted && (
															<Player
																accountId={configParams?.accountId as string}
																isVisible={
																	hasStorySwiperInitialised ? isActive : _index === storyPlaylistInitPosition
																}
																playerMedia={mediaId}
																settings={config}
																layout={isUsedOnMobile ? 'mobile' : 'desktop'}
																isMaintainProportions={
																	isUsedOnMobile ? config?.playbackOptions?.maintainProportions : false
																}
																playerId={`player-${mediaId}-${configParams.containerKey}`}
																token={getRestrictToken(currentMediaId ?? playlistId)}
																handlePlayerHover={handlePlayerHover}
																container={props.container}
																mediaData={media}
															/>
														)}
													</div>
												);
											}}
										</SwiperSlide>
									);
								})}
							</Swiper>
						)}

						{showControls && !isSticky && (
							<>
								{isPlaying !== undefined && (
									<Controls
										isMobile={isUsedOnMobile}
										playerHover={playerHover}
										settings={config}
										isInlinePlayer={isInlinePlayer}
										isMaintainProportions={isUsedOnMobile ? config?.playbackOptions?.maintainProportions : false}
										sideWindowOpen={isUsedOnMobile ? false : sideWindowOpen}
										onMouseEnter={() => {
											resetPlayerHoverRef.current = true;
											handlePlayerHover();
										}}
										onMouseLeave={() => {
											resetPlayerHoverRef.current = false;
											handlePlayerHover();
										}}
										container={props.container}
									/>
								)}
								<InteractOverlay
									isMobile={isUsedOnMobile}
									settings={config}
									isMaintainProportions={isUsedOnMobile ? config?.playbackOptions?.maintainProportions : false}
								/>
							</>
						)}
					</div>

					{isPlaying !== undefined && showControls && !isSticky && <ShoppingBasket settings={config} isMobile={isUsedOnMobile} />}
					{showControls && !isSticky && (
						<ChatOverlay
							showChatWindow={showChatWindow}
							settings={config}
							hideRightControls={hideRightControls}
							getRestrictToken={getRestrictToken}
							playerHover={playerHover}
						/>
					)}
					{showControls && !isSticky && showTranscriptionsModule && (
						<TranscriptionContainer settings={config} isMobile={isUsedOnMobile} />
					)}

					{playlistId !== '' && showControls && !adsPlaying && !isSticky && (
						<Playlist
							playlistId={playlistId}
							settings={config}
							playerHover={playerHover}
						/>
					)}
					<Poster
						isEditorPlayer={false}
						isMobile={isUsedOnMobile}
						playerConfigs={config}
					/>
				</Presenter>
			);
		},
		[isUsedOnMobile, playerMobileConfig, playerDesktopConfig, configParams, currentMediaId, playlistId, tokens, isPlaying, playerWidth, isUnder750, sideWindowOpen, shouldShowPasswordScreen, fromSceneEditor, screenBreakpointClass, screenType, mediaType, mediasData, getRestrictToken, handlePlayerHover, props.container, storyPlaylistInitPosition, hasStorySwiperInitialised, playerHover, isInlinePlayer, showChatWindow, hideRightControls, showTranscriptionsModule, adsPlaying, checkSubtitleBottomMargin, sceneConfig.currentScene, relatedMediasOfScene, setCurrentMediaId, isStoryPlaylist, showRegistrationForm, isSticky]
	);

	const playerLang = configParams?.language;
	const bcp47Lang = playerLang === 'dk' ? 'da' : playerLang === 'no' ? 'nb' : playerLang || 'en';

	// Rounded corners from the preset (Player styling), for the inline embed only - not the modal player.
	// Dropped in fullscreen, where they would show the black backdrop.
	const playerBorderRadius = getPlayerBorderRadius(isUsedOnMobile ? playerMobileConfig : playerDesktopConfig);
	const hasRoundedCorners = !!playerBorderRadius && isInlinePlayer && !isFullScreen;

	return (
		<div
			lang={bcp47Lang}
			className={joinClassNames(
				'qplayer-app',
				isLoadedByLoader ? 'qplayer-app--loader' : '',
				adsPlaying ? 'qplayer-app--ads-playing' : '',
				isSticky ? 'qplayer-app--sticky' : '',
				hasRoundedCorners ? 'qplayer-app--rounded' : '',
				screenBreakpointClass,
				mobileClassName,
			)}
			style={hasRoundedCorners ? { borderRadius: playerBorderRadius } : undefined}
			onMouseEnter={handlePlayerHover}
			onMouseMove={handlePlayerHover}
			onMouseLeave={() => {
				resetPlayerHoverRef.current = false;
				if (playerHoverTimeoutId.current) {
					clearTimeout(playerHoverTimeoutId.current);
				}
				if (isPlaying === false) {
					setPlayerHover(true);
					return;
				}

				// Keep the controls visible while a menu/dialog is open (e.g. a portalled MUI menu outside .qplayer-app).
				if (hasOpenMenu()) {
					return;
				}

				setPlayerHover(false);
			}}
			onMouseDown={closeSettingsMenus}
			onTouchMove={handlePlayerHover}
			onTouchEnd={(event) => {
				if (isIOS && ((event.target as HTMLElement)?.classList ?? " ")?.contains('mute-button')) {
					(window as any).GoBrain.widgets(currentPlayerId)?.volume?.(0.5);
				}

				if (isPlaying === false) {
					setPlayerHover(true);
					return;
				}
				resetPlayerHoverRef.current = false;
				handlePlayerHover();
			}}
			tabIndex={-1}
			onClick={handleQplayerAppClick}
			onKeyDown={(e) => {
				if (e.key === "Escape" && !isFullScreen) {
					closeModal();
				}
			}}
			onFocus={e => {
				e.preventDefault();
				e.stopPropagation();
			}}
		>
			{!loading && (
				<>
					{isSticky && (
						<StickyPlayerControls
							onRestore={() => configParams?.container?.scrollIntoView({ behavior: 'smooth', block: 'center' })}
							onClose={dismissStickyPlayer}
						/>
					)}
					{configParams?.privateAPI !== "true" && <RegisterForm
						playerId={currentPlayerId}
						isMobile={isUsedOnMobile || screenType === SCREEN_TYPE.small || screenType === SCREEN_TYPE.mobile}
						playerConfigs={isUsedOnMobile ? playerMobileConfig : playerDesktopConfig}
					/>}
					{!fromSceneEditor && renderPlayers(shouldShowPasswordScreen)}
					{shouldShowPasswordScreen && (
						<PasswordRestrictionContainer
							isMobile={isUsedOnMobile || screenType === SCREEN_TYPE.small || screenType === SCREEN_TYPE.mobile}
							playerConfigs={isUsedOnMobile ? playerMobileConfig : playerDesktopConfig}
							tokens={tokens}
						/>
					)}
					<ErrorContainer
						isMobile={isUsedOnMobile || screenType === SCREEN_TYPE.small || screenType === SCREEN_TYPE.mobile}
						playerConfigs={isUsedOnMobile ? playerMobileConfig : playerDesktopConfig}
					/>
					{!shouldShowPasswordScreen && !fromSceneEditor && <>
						<ShareMediaDialog
							playerConfigs={isUsedOnMobile ? playerMobileConfig : playerDesktopConfig}
							isMobile={isUsedOnMobile || screenType === SCREEN_TYPE.small || screenType === SCREEN_TYPE.mobile}
						/>
						<CountDown
							isMobile={isUsedOnMobile || screenType === SCREEN_TYPE.small || screenType === SCREEN_TYPE.mobile}
							playerConfigs={isUsedOnMobile ? playerMobileConfig : playerDesktopConfig}
							isInlinePlayer={isInlinePlayer}
							ref={countdownRef}
						/>
						{mediaType === 'scenario' && <TransitionLayer isMobile={isUsedOnMobile} />}
					</>}
					{fromSceneEditor && renderSceneEditorPlayer()}
				</>
			)}
		</div>
	);
};

export default App;
